import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { orderService } from "../../utils/services/orderService";
import CustomTypography from "../../globalStyles/fonts";
import CheckIcon from "@mui/icons-material/Check";
import styles from "./order-tracking.module.css";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { formatDateTime, trailEventDate } from "../../utils/formatDate";
// Dummy tracking data
const initialTrackingDetails = [
  {
    name: "Order Accepted",
    sl: 1,
    reached: false,
    subSteps: [],
    details: { text: "Your order has been placed and accepted.." },
  },
  {
    name: "Order Confirmed",
    sl: 2,
    reached: false,
    subSteps: [],
    details: { text: "Your order has been confirmed. We’re getting it ready." },
  },
  {
    name: "Label Created",
    sl: 3,
    reached: false,
    subSteps: [],
    details: { text: "Label Created for the shipments." },
  },
  {
    name: "Fulfilled",
    sl: 4,
    reached: false,
    subSteps: [],
    details: {
      text: "Your order has been fulfilled and is ready for the next steps.",
    },
  },
  {
    name: "Shipped",
    sl: 5,
    reached: false,
    subSteps: [],
    details: { text: "Your order has been shipped." },
  },
  {
    name: "In Transit - Origin",
    sl: 6,
    reached: false,
    subSteps: [],
    details: { text: "Your order is in transit." },
  },
  {
    name: "Customs Clearance",
    sl: 7,
    reached: false,
    subSteps: [],
    details: { text: "Custom cleared." },
  },
  {
    name: "In Transit - Destination",
    sl: 8,
    reached: false,
    subSteps: [],
    details: { text: "Your order is in transit." },
  },
  {
    name: "Out for Delivery",
    sl: 9,
    reached: false,
    subSteps: [],
    details: {
      text: "Out for Delivery – The package is on its way to the delivery address.",
    },
  },
  { name: "Delivered", sl: 10, reached: false, subSteps: [] },
];

// Reusable Step Circle Component
const StepCircle = ({ reached, isLastReached }) => (
  <div
    style={{
      width: "24px",
      height: "24px",
      borderRadius: "50%",
      backgroundColor: reached || isLastReached ? "green" : "white",
      marginRight: "8px",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      border: !reached && !isLastReached ? "1px solid #D9D9D9" : "",
    }}
  >
    {(reached || isLastReached) && (
      <CheckIcon style={{ color: "#fff", fontSize: "16px" }} />
    )}
  </div>
);

// Reusable SubStep Circle Component
const SubStepCircle = () => (
  <div
    style={{
      width: "24px",
      height: "24px",
      borderRadius: "50%",
      marginRight: "8px",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      border: "1px solid #D9D9D9",
    }}
  >
    <div
      style={{
        height: 12,
        width: 12,
        borderRadius: 50,
        backgroundColor: "green",
      }}
    />
  </div>
);

// Reusable Vertical Line Component
const VerticalLine = ({ reached, isLast, isLastReached, isSubStep }) => (
  <div
    style={{
      minHeight: isSubStep ? 50 : "calc(100% - 24px)",
      marginLeft: 12,
      marginTop: 5,
      borderLeft:
        reached || isLastReached ? "1px dotted green" : "1px dotted #757477",
      width: 2,
      position: isSubStep ? "static" : "absolute",
      top: isSubStep ? 0 : 24,
    }}
  />
);

// Tracking Step Component
const TrackingStep = ({
  step,
  index,
  activeStep,
  lastReached,
  handleStepClick,
  trackData,
}) => (
  <div
    onClick={() => handleStepClick(index)}
    style={{
      marginBottom: "10px",
      cursor: "pointer",
      position: "relative",
      backgroundColor: index === activeStep ? "#E4FFE8" : "",
    }}
  >
    <div style={{ display: "flex", alignItems: "center" }}>
      <StepCircle
        reached={step.reached}
        isLastReached={step.sl < lastReached?.sl}
      />
      <span
        style={{
          fontSize: 14,
          color: step.reached ? "green" : "var(--text-tertiary)",
          fontWeight: "600",
        }}
      >
        {step.name}
      </span>
    </div>
    <div style={{ display: "flex" }}>
      {index !== trackData.length - 1 && (
        <VerticalLine
          reached={step.reached}
          isLastReached={step.sl < lastReached?.sl}
        />
      )}
      <div
        style={{
          paddingLeft: "20px",
          textAlign: "left",
          overflowY: "auto",
          marginTop: "8px",
        }}
      >
        {step.details && step.sl < lastReached?.sl && (
          <>
            <CustomTypography style={{ fontSize: 10 }} variant="body2">
              {step.subSteps?.[0]?.message || ""}
            </CustomTypography>
            <CustomTypography
              style={{ marginTop: 1, fontSize: 10 }}
              variant="caption"
            >
              {formatDateTime(trailEventDate(step.subSteps?.[0]), "")}
            </CustomTypography>
          </>
        )}
        {step.subSteps?.length > 0 &&
          step.sl <= lastReached?.sl &&
          step.subSteps
            .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
            .map((subStep, subIndex) => (
              <div
                key={subStep.sl}
                style={{
                  marginBottom: "5px",
                  position: "relative",
                  marginTop: 10,
                }}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <SubStepCircle />
                  <span
                    style={{
                      fontSize: 12,
                      color:
                        step.reached || step.sl < lastReached?.sl
                          ? "green"
                          : "var(--text-tertiary)",
                      fontWeight: "600",
                    }}
                  >
                    {subStep.message}
                  </span>
                </div>
                <div style={{ display: "flex" }}>
                  {trackData[index].subSteps.length > 1 &&
                  subIndex !== trackData[index].subSteps.length - 1 ? (
                    <VerticalLine reached={true} isSubStep />
                  ) : (
                    <div style={{ marginLeft: 14 }} />
                  )}
                  <div
                    style={{
                      paddingLeft: "20px",
                      textAlign: "left",
                      maxHeight: "100px",
                      overflowY: "auto",
                      marginTop: "5px",
                      marginLeft: 3,
                    }}
                  >
                    {subStep?.comment ? (
                      <CustomTypography
                        style={{ fontSize: 13, marginBottom: "3px" }}
                        variant="body2"
                      >
                        {subStep.comment}
                      </CustomTypography>
                    ) : (
                      <></>
                    )}
                    <CustomTypography
                      style={{ fontSize: 13, marginBottom: "3px" }}
                      variant="body2"
                    >
                      {subStep.location || ""}
                    </CustomTypography>
                    <CustomTypography
                      style={{ fontSize: 10, marginTop: "5px" }}
                      variant="body2"
                    >
                      {(subStep.time || subStep.createdAt) &&
                        formatDateTime(subStep.time || subStep.createdAt)}
                    </CustomTypography>
                  </div>
                </div>
              </div>
            ))}
      </div>
    </div>
  </div>
);

// Mobile Dialog Component
const MobileTrackingDialog = ({ open, onClose, activeStep, trackData }) => (
  <Dialog
    className={styles.showForMobile}
    open={open}
    onClose={onClose}
    maxWidth="sm"
    fullWidth
  >
    <DialogTitle>
      Tracking Details
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <DialogContent dividers>
      {activeStep !== null && (
        <div
          style={{
            padding: "20px",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
            maxHeight: 500,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", padding: 20 }}>
            <StepCircle reached={trackData[activeStep].reached} />
            <span
              style={{
                color: trackData[activeStep].reached
                  ? "green"
                  : "var(--text-tertiary)",
                fontWeight: "600",
              }}
            >
              {trackData[activeStep]?.name}
            </span>
          </div>
          {trackData[activeStep].subSteps?.length > 0 ? (
            trackData[activeStep].subSteps.map((step, index) => (
              <div
                key={step.sl}
                style={{
                  marginBottom: "5px",
                  position: "relative",
                  marginLeft: 40,
                }}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <SubStepCircle />
                  <span
                    style={{
                      color: step.reached ? "green" : "var(--text-tertiary)",
                      fontWeight: "600",
                    }}
                  >
                    {step.order_number}
                  </span>
                </div>
                <div style={{ display: "flex" }}>
                  {index !== trackData[activeStep].subSteps.length - 1 && (
                    <VerticalLine reached={true} isSubStep />
                  )}
                  <div
                    style={{
                      paddingLeft: "20px",
                      textAlign: "left",
                      maxHeight: "100px",
                      overflowY: "auto",
                      marginTop: "8px",
                    }}
                  >
                    <CustomTypography style={{ fontSize: 10 }} variant="body2">
                      {formatDateTime(step.createdAt)}
                    </CustomTypography>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <p>No substeps available</p>
          )}
        </div>
      )}
    </DialogContent>
  </Dialog>
);

export default function OrderTrackingHistory() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const trackingStatus = searchParams.get("status");
  const currentUser = JSON.parse(localStorage.getItem("user"));
  const [trackData, setTrackData] = useState(initialTrackingDetails);
  const [orderTrackData, setOrderTrackData] = useState([]);
  const [activeStep, setActiveStep] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleStepClick = (stepIndex) => {
    setActiveStep(stepIndex);
    // setDrawerOpen(true); // Uncomment if drawer functionality is needed
  };

  const handleDrawerClose = () => setDrawerOpen(false);

  const getSubSteps = (item) => {
    const data = orderTrackData.find(
      (x) => x.status.toLowerCase() === item.name.toLowerCase(),
    );
    if (data) return data.values;
    const subStep = initialTrackingDetails.find(
      (xx) => xx.name.toLowerCase() === item.name?.toLowerCase(),
    );
    const fallbackParent = orderTrackData.find(
      (r) => r.status === initialTrackingDetails[item.sl]?.name,
    )?.parent;
    return [
      {
        message: subStep?.details?.text,
        createdAt: fallbackParent?.createdAt,
        time: fallbackParent?.time,
      },
    ];
  };

  const setTrackingData = () => {
    const isCancelled = orderTrackData.find((x) => x.status === "Cancelled");
    const isConfirmed = orderTrackData.find(
      (x) => x.status === "Order Confirmed",
    );
    const updatedTrack = isCancelled
      ? [
          {
            name: "Order Accepted",
            sl: 1,
            reached: false,
            subSteps: [],
            details: { text: "Your order has been placed and Accepted." },
          },
          {
            name: "Cancelled",
            sl: 9,
            reached: false,
            subSteps: [],
            details: { text: "Your order has been cancelled." },
          },
        ]
      : isConfirmed
        ? initialTrackingDetails
        : initialTrackingDetails;

    const allTrackingStatus = orderTrackData.map((x) => x.status);
    setTrackData(
      updatedTrack.map((item) => ({
        ...item,
        reached: allTrackingStatus?.includes(item.name),
        subSteps: getSubSteps(item),
      })),
    );
  };

  const fetchTrackingData = async () => {
    try {
      const response = await orderService.fetchOrderTrailForTracking(id);
      setOrderTrackData(response);
    } catch (error) {
      console.error("Fetch failed:", error);
    }
  };

  useEffect(() => {
    if (id) fetchTrackingData();
  }, [id]);

  useEffect(() => {
    if (orderTrackData.length) setTrackingData();
  }, [orderTrackData]);

  const getLastReachedStep = () =>
    trackData.reduce(
      (last, current) => (current.reached ? current : last),
      null,
    );

  const lastReached = getLastReachedStep();

  return (
    <div
      className="scrollableDiv"
      style={{
        display: "flex",
        justifyContent: "center",
        overflowY: "scroll",
        width: "100%",
        height: "100%",
        position: "relative",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        padding: 20,
      }}
    >
      <div className={styles.container}>
        <div
          className={styles.leftContainer}
          style={{
            marginLeft: "30px",
            padding: 15,
            maxHeight: currentUser?._id ? "98%" : "92%",
            display: "flex",
            flexDirection: "column",
            borderWidth: 2,
            borderRadius: 8,
            backgroundColor: "var(--bg-paper)",
            width: "95%",
          }}
        >
          <header className={styles.trackingHeader}>
            <h1 className={styles.trackingNumber}>
              Tracking Number:{" "}
              <span style={{ fontSize: 17 }}>
                {trackingStatus && trackingStatus !== "undefined"
                  ? trackingStatus
                  : ""}
              </span>
            </h1>
          </header>
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#E4FFE8",
              borderRadius: "4px",
              padding: "6px 8px",

              width: "fit-content",
              textAlign: "center",
              border: "1px solid #06B217",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Poppins",
                textAlign: "center",
                color: "#06B217",
                fontWeight: 500,
              }}
            >
              {lastReached?.name}
            </Typography>
          </Box>
          <section className={styles.orderInfo}>
            <div className={styles.orderDetails}>
              <div className={styles.orderDateWrapper}>
                <span className={styles.orderDateLabel}>Order date:</span>
                {trackData?.find((x) => x.name === "Order Accepted")?.subSteps
                  ?.length ? (
                  <time className={styles.orderDate} dateTime="2024-08-16">
                    {new Date(
                      trailEventDate(
                        trackData.find((x) => x.name === "Order Accepted")
                          .subSteps[0],
                      ),
                    ).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </time>
                ) : null}
              </div>
              <div className={styles.separator} />
              <div className={styles.estimatedDelivery}>
                <img src="/truck-tick.png" alt="" className={styles.icon} />
                <span className={styles.estimatedDeliveryText}>
                  Estimated delivery:{" "}
                </span>
              </div>
            </div>
          </section>
          <div
            style={{
              width: "100%",
              marginTop: 30,
              maxHeight: "99%",
              display: "flex",
              flexDirection: "column",
              overflow: "auto",
            }}
          >
            {trackData?.map((step, index) => (
              <TrackingStep
                key={step.sl}
                step={step}
                index={index}
                activeStep={activeStep}
                lastReached={lastReached}
                handleStepClick={handleStepClick}
                trackData={trackData}
              />
            ))}
          </div>
        </div>
        {drawerOpen && (
          <div
            className={styles.rightContainer}
            style={{
              marginLeft: 10,
              borderWidth: 2,
              borderRadius: 8,
              display: "flex",
              overflow: "auto",
              flexDirection: "column",
              maxHeight: "95%",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: currentUser?._id ? 10 : 0,
                right: 20,
                cursor: "pointer",
              }}
              onClick={handleDrawerClose}
            >
              <CloseIcon />
            </div>
            {activeStep !== null && (
              <div style={{ padding: "20px" }}>
                <div
                  style={{ display: "flex", alignItems: "center", padding: 20 }}
                >
                  <StepCircle reached={trackData[activeStep].reached} />
                  <span
                    style={{
                      color: trackData[activeStep].reached
                        ? "green"
                        : "var(--text-tertiary)",
                      fontWeight: "600",
                    }}
                  >
                    {trackData[activeStep]?.name}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
        <MobileTrackingDialog
          open={drawerOpen}
          onClose={handleDrawerClose}
          activeStep={activeStep}
          trackData={trackData}
        />
      </div>
    </div>
  );
}
